import React, { useState } from 'react';
import { content, settings } from '../content';
import { useGithubStats } from '../hooks/useGithubStats';
import { SectionId } from '../types';
import Terminal from './Terminal';
import {
  CloseIcon,
  DiamondIcon,
  MenuIcon,
  navIcons,
  ProjectsIcon,
  PulseIcon,
  RepoIcon,
  socialIcons,
} from './Icon';
import '../styles/Sidebar.css';

const { nav, navLabel, sidebar, author, socials, projects, testimonials, blog, ui } =
  content;

/* A nav entry leading to an empty page is worse than no entry: the reviews
   section only exists while there are real reviews in it, and the blog only
   while something has actually been written. */
const navItems = nav.filter(({ id }) => {
  if (id === 'testimonials') return testimonials.items.length > 0;
  if (id === 'blog') return blog.posts.length > 0;
  return true;
});

type SidebarProps = {
  active: SectionId;
  onNavigate: (id: SectionId) => void;
};

const Sidebar = ({ active, onNavigate }: SidebarProps) => {
  const { stats, loading } = useGithubStats();
  // Desktop shows the whole column; below 900px everything but the brand line
  // hides behind this toggle, so the visitor lands on the work, not on a menu.
  const [menuOpen, setMenuOpen] = useState(false);

  const rows = [
    {
      icon: ProjectsIcon,
      value: String(projects.length),
      label: sidebar.stats.projects,
      live: false,
    },
    {
      icon: PulseIcon,
      value: stats.contributionsLastYear?.toLocaleString('ru-RU') ?? null,
      label: sidebar.stats.contributions,
      live: true,
    },
    {
      icon: RepoIcon,
      value: stats.publicRepos !== null ? String(stats.publicRepos) : null,
      label: sidebar.stats.repos,
      live: true,
    },
    {
      icon: DiamondIcon,
      value: settings.availableForWork ? sidebar.available : sidebar.busy,
      label: sidebar.stats.availability,
      live: false,
    },
  ];

  return (
    <aside className={`sidebar${menuOpen ? ' sidebar--open' : ''}`}>
      <div className="sidebar__top">
        {/* A face and a name, where an invented studio brand used to be. A
            person is what the visitor is deciding to hire, and "IdeaProjects"
            told them nothing they could act on. */}
        <header className="brand">
          <div className="author__avatar">
            {author.avatar ? (
              // Decorative: the name is spelled out right next to it, and a
              // screen reader announcing it twice helps nobody.
              <img src={author.avatar} alt="" width={44} height={44} />
            ) : (
              <span className="author__initials">{author.name.slice(0, 1)}</span>
            )}
            {settings.availableForWork && (
              <span className="author__online" aria-label={sidebar.availableHint} />
            )}
          </div>
          <div className="brand__body">
            <p className="brand__name">
              {author.name}
              <span className="dot">.</span>
            </p>
            <p className="brand__role">{author.role}</p>
          </div>
        </header>

        <button
          type="button"
          className="sidebar__burger"
          aria-label={menuOpen ? ui.menuClose : ui.menuOpen}
          aria-expanded={menuOpen}
          aria-controls="sidebar-panel"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <CloseIcon size={20} /> : <MenuIcon size={20} />}
        </button>
      </div>

      <div className="sidebar__scroll" id="sidebar-panel">
        <nav className="nav" aria-label={navLabel}>
          {navItems.map(({ id, label }) => {
            const Icon = navIcons[id];

            return (
              <button
                key={id}
                type="button"
                className={`nav__item${active === id ? ' nav__item--active' : ''}`}
                aria-current={active === id ? 'page' : undefined}
                onClick={() => {
                  onNavigate(id);
                  setMenuOpen(false);
                }}
              >
                <Icon size={17} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>

        <section className="status">
          <h2 className="status__title">
            {sidebar.statusTitle}
            <a
              className="status__source"
              href={`https://github.com/${settings.githubUser}`}
              target="_blank"
              rel="noreferrer"
              title={sidebar.statusSourceHint}
            >
              {sidebar.statusSource}
            </a>
          </h2>

          <ul className="status__list">
            {rows.map(({ icon: Icon, value, label, live }) => {
              // A live row that resolved to nothing means the API was rate
              // limited or offline — hide it rather than show a fake number.
              if (live && value === null && !loading) return null;

              return (
                <li key={label} className="stat">
                  <Icon size={17} className="stat__icon" />
                  <div className="stat__body">
                    {value === null ? (
                      <span className="stat__skeleton" aria-hidden />
                    ) : (
                      <span className="stat__value">{value}</span>
                    )}
                    <span className="stat__label">{label}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        <Terminal />
      </div>

      <footer className="socials">
        {socials.map(({ id, label, href }) => {
          const Icon = socialIcons[id];

          return (
            <a
              key={id}
              className="socials__link"
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              title={label}
            >
              <Icon size={20} />
            </a>
          );
        })}
      </footer>
    </aside>
  );
};

export default Sidebar;
